import React, { useMemo } from 'react'
import {
  useWishlist,
  useLineItem,
  usePrice,
  useConfigState,
  useGlobalLabels,
  useLabelsQuery,
} from '@sentecacommerce-theme/lib'
import styled, { keyframes } from 'styled-components'
import { Link } from '@sentecacommerce-theme/lib'
import { Price, LazyResponsiveImage, Rating, IconShop } from '../'
import { WishlistButton } from '../WishlistButton'
import { BasketButton } from '../BasketButton'
import { Typography } from '../Typography'
import { IconVideoCamera } from '../Icons'
import { mapLabels } from '../../utils/LabelUtils'

export const LineItem = ({ data }: { data: any }) => {
  const {
    name,
    images,
    slug,
    _id,
    offerId,
    variantId,
    ratings,
    sku,
    inventory,
    price: priceProps,
    discountedPrice: discountedPriceProps,
    getCustomFieldByKey,
    offer,
    variantOptions,
    variantsCount,
    labels,
  } = useLineItem(data)

  const config = useConfigState()

  const offerValidFrom =
    offer?.discount &&
    new Date(offer?.discount?.validFrom).toLocaleDateString('bg-BG', {
      month: '2-digit',
      day: '2-digit',
    })

  const offerValidTo =
    offer?.discount &&
    new Date(offer?.discount?.validTo)
      .toLocaleDateString('bg-BG', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
      })
      .slice(0, -3)

  const hasExpirableOffer = Boolean(offerValidTo && offerValidFrom)
  const globalLabels = useGlobalLabels()

  const hideRegularPrice = getCustomFieldByKey?.(
    config?.customFieldKeys?.hideRegularPrice
  )

  const isOnlyInPhysicalStores = getCustomFieldByKey?.(
    config?.customFieldKeys?.isOnlyInPhysicalStores
  )

  const isOnlyInHippoland = getCustomFieldByKey?.(
    config?.customFieldKeys?.isOnlyInHippoland
  )
  const isInAction = getCustomFieldByKey(config.customFieldKeys.isAction)

  const discountIcon = getCustomFieldByKey(config.customFieldKeys.discountIcon)

  const { data: labelsData } = useLabelsQuery({ labels })
  const mappedLabels = useMemo(
    () =>
      mapLabels({
        labels: labelsData?.results ?? [],
        isListing: true,
        isInAction: isInAction,
      }),
    [labelsData?.results, isInAction]
  )

  const getPrice = () => {
    if (!hideRegularPrice) return priceProps

    if (discountedPriceProps?.centAmount) return discountedPriceProps

    return priceProps
  }

  const {
    price,
    discountPrice,
    discountPercentage,
    discountDifference,
  } = usePrice({
    price: getPrice(),
    discountedPrice: !hideRegularPrice ? discountedPriceProps : undefined,
  })

  const { isInWishlist } = useWishlist({ productId: _id, variantId })

  const isNew =
    getCustomFieldByKey?.(config?.customFieldKeys?.isNew) ||
    (Date.now() > getCustomFieldByKey?.(config?.customFieldKeys?.newFromDate) &&
      Date.now() < getCustomFieldByKey?.(config?.customFieldKeys?.newToDate))

  const hasVideo = getCustomFieldByKey?.(config?.customFieldKeys?.hasVideo)
  const isOnlineOnly = getCustomFieldByKey?.(
    config?.customFieldKeys?.isOnlineOnly
  )

  const isBestSelling = false

  const discountValue = discountDifference?.value

  const showBasket = Boolean(
    !isOnlyInPhysicalStores &&
      inventory?.hasAvailableQuantity &&
      variantOptions < 1 &&
      variantsCount < 2
  )

  return (
    <Link passHref href={`/${slug}/p`}>
      <Container isInWishlist={isInWishlist}>
        <ImageContainer>
          <LazyResponsiveImage
            breakpointSizes={{
              xs: '50vw',
              sm: '50vw',
              md: '30vw',
              lg: '220px',
              xl: '220px',
              xxl: '220px',
            }}
            defaultImageWidth="40vw"
            imagesMap={images?.[0]}
            alt={name}
          />
          {hasExpirableOffer && (
            <OfferTimestampContainer>
              <Typography variant="9">
                {`${globalLabels?.offerIsValid} ${offerValidFrom}-${offerValidTo}`}
              </Typography>
            </OfferTimestampContainer>
          )}
        </ImageContainer>
        <ProductInfoContainer>
          <SKUContainer>{`${globalLabels?.sku.toUpperCase()}: ${sku}`}</SKUContainer>
          <ProductTitle>{name}</ProductTitle>
          <RatingContainer>
            <Rating value={ratings.averageRating} productId={_id || slug} />
            {hasVideo ? <IconVideoCamera /> : null}
          </RatingContainer>
          {isOnlyInPhysicalStores && (
            <OnlyInPhysicalStoresContainer>
              <StyledIconShop />
              <Typography variant="9">
                {globalLabels?.availableOnlyInPhysicalStores}
              </Typography>
            </OnlyInPhysicalStoresContainer>
          )}
        </ProductInfoContainer>
        <CTAWrapper hasExpirableOffer={hasExpirableOffer}>
          <PriceWrapper>
            <Price
              price={price.value}
              currency={price.currency}
              size="small"
              hideDiscountPercentage
              discountedPrice={discountPrice?.value}
              hideRegularPrice={hideRegularPrice}
            />
          </PriceWrapper>
          {showBasket && (
            <CartWrapper>
              <BasketButton
                productId={_id}
                variantId={variantId}
                type="icon"
                maxQuantity={Math.min(
                  inventory?.availableQuantity,
                  offer?.maxQuantity || config.inventory.maxQuantity
                )}
                offerId={offerId}
                canOrder={inventory.hasAvailableQuantity}
              />
            </CartWrapper>
          )}
        </CTAWrapper>
        <FavIconWrapper>
          <WishlistButton variantId={variantId} productId={_id} />
        </FavIconWrapper>
        {mappedLabels?.map((label) => {
          if (label.isIcon) {
            return (
              <LabelIcon
                style={{ ...label.appearance, ...label.location }}
                key={label._id}
                src={label.icon}
                alt={label.name}
              />
            )
          }
        })}
        <BadgeListContainer>
          {isInAction && (
            <ActionImageContainer>
              <img
                style={{ width: '5rem' }}
                alt="Hippoland Sale"
                src="https://www.hippoland.net/media/catalog/category/label-action-icon.svg"
              />
            </ActionImageContainer>
          )}
          {isOnlyInHippoland && (
            <YellowBadge>{globalLabels?.onlyInHippoland}</YellowBadge>
          )}

          {isNew && <NewBadge>{globalLabels?.new}</NewBadge>}
          {isOnlineOnly && <BlueBadge>{globalLabels.onlyOnline}</BlueBadge>}

          {isBestSelling && <MostSaled>{globalLabels?.bestSeller}</MostSaled>}

          {!isInAction && Boolean(discountPercentage) && (
            <DiscountBadge>-{discountPercentage}%</DiscountBadge>
          )}

          {!isInAction && !Boolean(discountPercentage) && discountValue && (
            <DiscountBadge>
              -{Math.floor(discountValue)}
              <small>{price.currency}</small>
            </DiscountBadge>
          )}
          {discountIcon && (
            <DiscountIcon
              src={((discountIcon as unknown) as string) || 'Hippoland'}
            />
          )}
        </BadgeListContainer>
      </Container>
    </Link>
  )
}

const LabelIcon = styled.img`
  position: absolute;
  z-index: 20;
`
const StyledIconShop = styled(IconShop).attrs({ className: 'mr-2' })`
  width: 2.5rem;
  fill: ${({ theme }) => theme.palette.danger};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    width: 2rem;
  }
`
const OnlyInPhysicalStoresContainer = styled.div.attrs({
  className: 'd-flex align-items-center',
})`
  position: absolute;
  bottom: -1.4rem;
  color: ${({ theme }) => theme.palette.danger};
`

const ActionImageContainer = styled.div`
  margin-left: 1rem;
  margin-bottom: 0.5rem;
`

const OfferTimestampContainer = styled.div.attrs({
  className: 'd-flex align-items-center pl-3 py-2 ',
})`
  position: absolute;
  text-align: left;
  bottom: 0;
  width: 100%;
  color: ${({ theme }) => theme.palette.silverTree};
  background-color: #e3f7db;
`

const BadgeListContainer = styled.div.attrs({
  className: 'd-flex flex-column align-items-start',
})`
  position: absolute;
  top: 1rem;
  left: -0.1rem;
`

const Badge = styled.div.attrs({
  className: 'py-0 px-2 mb-3',
})`
  min-width: 5.1rem;
  background-color: var(--badge-color);
  color: var(--badge-text-color);
  font-weight: bold;
  font-size: 1rem;
  height: 2.4rem;
  text-align: center;
  text-transform: uppercase;
  line-height: 2.4rem;
  z-index: 100;
  position: relative;

  & > small {
    font-size: 0.8rem;
  }

  &:after {
    content: '';
    position: absolute;
    right: -1.6rem;
    top: 0px;
    z-index: 14;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 2.4rem 1.6rem 0px 0px;
    border-color: var(--badge-color) transparent transparent transparent;
  }
`

const YellowBadge = styled(Badge)`
  --badge-color: ${({ theme }) => theme.palette.yellow};
  --badge-text-color: ${({ theme }) => theme.palette.secondary};
`

const BlueBadge = styled(Badge)`
  --badge-color: #1480c8;
  --badge-text-color: ${({ theme }) => theme.palette.secondary};
`

const NewBadge = styled(Badge)`
  --badge-color: ${({ theme }) => theme.palette.discounted};
  --badge-text-color: ${({ theme }) => theme.palette.secondary};
`

const DiscountBadge = styled(Badge)`
  --badge-color: ${({ theme }) => theme.palette.discounted};
  --badge-text-color: ${({ theme }) => theme.palette.secondary};
`

const MostSaled = styled(Badge)`
  --badge-color: ${({ theme }) => theme.palette.salmon};
  --badge-text-color: ${({ theme }) => theme.palette.secondary};
`
const FavIconWrapper = styled.div`
  color: ${({ theme }) => theme.palette.silverTree};
  cursor: pointer;
  position: absolute;
  top: 0;
  right: 0;

  & svg {
    width: 3.2rem;
    height: 3.2rem;
    color: ${({ theme }) => theme.palette.silverTree};
    transition: background-position 1s steps(28);
    transition-duration: 0s;
    position: absolute;
    right: 1rem;
    top: 0.5rem;
    z-index: 100;
    transition: all 0.3s;

    @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
      opacity: 0;
    }

    &:hover {
      transform: scale(1.3);
    }
  }
`

const Container = styled.a<{ isInWishlist: boolean | undefined }>`
  text-decoration: none;
  display: block;
  width: 100%;
  border-radius: 1rem;
  border: 0.1rem solid transparent;
  position: relative;
  background-color: ${({ theme }) => theme.palette.secondary};
  box-shadow: 0 0 0.8rem #e6e5e5;
  transition: box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  transform: translateY(0);
  padding-top: 4rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md.max}) {
    &:hover {
      box-shadow: 0 0.5rem 1.6rem rgba(0, 0, 0, 0.15);
      transform: translateY(-0.6rem);
    }

    ${FavIconWrapper} {
      @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
        & svg {
          opacity: ${({ isInWishlist }) => (!isInWishlist ? '0' : '1')};
        }
      }
    }
    &:hover ${FavIconWrapper} {
      & svg {
        opacity: 1;
      }
    }
  }
`

const ImageContainer = styled.div`
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  overflow: hidden;
  position: relative;
  text-align: center;
  cursor: pointer;

  @media only screen and (${({ theme }) => theme.breakpoints.sm.max}) {
    height: auto;
  }
`

const ProductInfoContainer = styled.div.attrs({
  className: 'px-3',
})`
  position: relative;

  @media only screen and (max-width: 480px) {
    margin-bottom: 0 !important;
  }

  @media only screen and (${({ theme }) => theme.breakpoints.xs.max}) {
    margin-bottom: 1rem;
  }

  cursor: pointer;
`

const ProductTitle = styled(Typography).attrs({
  'data-blogger': '',
  variant: 6,
  className: ' mb-3',
})`
  color: ${({ theme }) => theme.palette.greyTitle};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-height: 1.6rem;
  min-height: 3.2rem;
  max-height: 3.2rem;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media only screen and (${({ theme }) => theme.breakpoints.sm.max}) {
    font-size: 1.4rem;
    margin-top: 1rem;
  }
`

const showBasketSmooth = keyframes`
  0% {
    opacity: 0
  }
  100% {
    opacity: 1
  }
`

const CartWrapper = styled.div.attrs({
  className: 'd-flex align-items-end justify-content-end',
})<{ isOutOfStock?: boolean }>`
  position: relative;

  min-width: 3.5rem;

  cursor: pointer;

  transition: all 0.3s;

  animation: ${showBasketSmooth} 0.3s ease-in normal forwards;

  & > button {
    padding-left: 0 !important;
    padding-right: 0 !important;
    min-height: initial;
  }

  @media only screen and (${({ theme }) => theme.breakpoints.sm.max}) {
    margin-right: 0.5rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md.max}) {
    transition: all 0.3s;
    margin-bottom: 0.1rem;

    animation: ${showBasketSmooth} 0.3s ease-in normal forwards;

    &:hover {
      transform: ${(props) => (props.isOutOfStock ? 'scale(1)' : 'scale(1.3)')};
    }
  }
`

const CTAWrapper = styled.div.attrs({
  className: 'd-flex align-items-end justify-content-between pt-0 px-3 pb-4',
})<{ hasExpirableOffer: boolean }>`
  position: relative;
  height: 6rem;
  @media only screen and (${({ theme }) => theme.breakpoints.sm.max}) {
    padding: 0 0 1.5rem 1.5rem;
  }
`

const PriceWrapper = styled.div`
  color: ${({ theme }) => theme.palette.primary};
  font-size: 1.8rem;
  width: 100%;

  @media only screen and (${({ theme }) => theme.breakpoints.sm.max}) {
    font-size: 1.4rem;
  }
`

const SKUContainer = styled(Typography).attrs({
  variant: 8,
  as: 'div',
  color: 'greyTitle',
  className: 'my-2',
})`
  text-align: right;
`
const RatingContainer = styled.div.attrs({
  className: 'd-flex align-items-center justify-content-start',
})`
  & > svg {
    margin-left: 1.2rem;
    width: 2.2rem;
    margin-bottom: 1.2rem;
    color: ${({ theme }) => theme.palette.grey};
    @media (max-width: ${({ theme }) => theme.breakpoints.sm.max}) {
      width: 1.8rem;
      margin-left: 0.6rem;
    }
  }
`

const DiscountIcon = styled.img`
  max-width: 4rem;
  width: 5rem;
`
